import { drawSprite, drawSpriteCached, silhouette, tintSprite, type Painter } from '../draw/painter';
import { paintTerrainSteps } from './ground-edges';
import type { PlacedBuilding } from './buildings';
import { floorPlan } from './interior';
import { PathRoads } from './path-adapter';
import type { RoadHit } from './roads';
import { CHUNK, RF_H, RF_HC, RF_HEVEN, RF_SIDEWALK, RF_V, RF_VC, RF_VEVEN, type Chunk, type WorldGen } from './generator';
import { hash01 } from './rng';

/**
 * Draw the static ground layer of a chunk (terrain, roads, decor) with any Painter.
 * `scale` = px per tile; origin (ox,oy) = device position of the chunk's top-left.
 * Used by the game (Canvas, cached per chunk) and by the content tool (PNG map).
 */
/** Is this ballast tile part of a platform: within 14 tiles of a level-0 road crossing the railway inside a town (where trains halt)? */
function platformAt(gen: WorldGen, x: number, y: number, railAxis: 'v' | 'h'): boolean {
  const along = railAxis === 'v' ? y : x, across = railAxis === 'v' ? x : y;
  const kx = Math.round(along / 128), cross = kx * 128, dist = Math.abs(along - cross);
  if (dist < 4 || dist > 16) return false;
  const perp: 'v' | 'h' = railAxis === 'v' ? 'h' : 'v';
  const road = gen.net.lineDef(perp, 0, kx, Math.floor(across / 128));
  if (!road || road.class === 'rail') return false;
  return gen.fields.density(x, y) >= 0.36;
}

// (track 2) the ground's colours and its curved edges live in ground-edges.ts; `groundColour` stays exported from here for its callers
export { groundColour } from './ground-edges';

export function renderChunkGround(p: Painter, gen: WorldGen, c: Chunk, ox: number, oy: number, scale: number, brokenRails?: ReadonlySet<string>, neighbour?: (cx: number, cy: number) => Chunk | undefined): void {
  const it = renderChunkGroundSteps(p, gen, c, ox, oy, scale, brokenRails, neighbour); while (!it.next().done);
}
/**
 * (owner, Sep 25: the jerking at speed) `renderChunkGround` a slice at a time: a generator that stops between the passes and every
 * few rows of the terrain, so the game paints the chunks ahead of the view in a frame's spare time instead of 10–40 ms at once
 * (src/engine/world-stream.ts `prepaint`). One in progress at a time (the terrain's scratch grids are shared).
 */
export function* renderChunkGroundSteps(p: Painter, gen: WorldGen, c: Chunk, ox: number, oy: number, scale: number, brokenRails?: ReadonlySet<string>, neighbour?: (cx: number, cy: number) => Chunk | undefined): Generator<void, void, void> {
  p.begin(ox, oy, scale);
  const f = gen.fields;
  // the terrain: every tile's ground colour under the relief, the shore and the land borders as curves, the terrace
  // steps as contour lines (owner, Sep 11: no more pixelated shorelines) — src/world/ground-edges.ts
  yield* paintTerrainSteps(p, gen, c, neighbour);
  yield;
  // roads on top
  const both = RF_V | RF_H;
  const at = (lx: number, ly: number) => (lx < 0 || ly < 0 || lx >= CHUNK || ly >= CHUNK ? -1 : ly * CHUNK + lx);
  const isWater = (i: number) => { const t = gen.terrainList[c.terrain[i]]; return !t.walkable && !t.buildable; };
  // two passes over the road tiles: the surfaces first, then (on the path network) the roads stroked along their curves —
  // kerbs and centre lines as lines, not tile-steps (§40) — and then everything drawn *on* a surface: crosswalks, rails,
  // barriers, railings, the lattice's tile-wise centre line. Anything stroked has to sit between the two.
  const strokes = gen.paths && gen.net instanceof PathRoads ? gen.net.strokesIn(c.x0, c.y0, c.x0 + CHUNK, c.y0 + CHUNK, true) : undefined; // (rails too: the ground railway is stroked along its curve like the roads — owner, Sep 11: the tile-stepped track did not meet the viaduct)
  for (const phase of [0, 1] as const) {
  if (phase === 1 && strokes) { yield; strokePaths(p, gen, c, strokes, isWater, brokenRails); yield; }
  for (let ly = 0; ly < CHUNK; ly++) {
    for (let lx = 0; lx < CHUNK; lx++) {
      const i = ly * CHUNK + lx;
      const ri = c.road[i];
      if (!ri) continue;
      const r = gen.roadList[ri - 1];
      const fl = c.roadFlags[i];
      const bridge = isWater(i);
      if (phase === 0) { if (strokes) continue; if (fl & RF_SIDEWALK) p.rect(lx, ly, 1, 1, bridge ? '#8a8f96' : r.sidewalkColor ?? '#b0b0ab'); else if (r.class !== 'rail') p.rect(lx, ly, 1, 1, bridge ? '#5a5d63' : r.color); continue; }
      if (fl & RF_SIDEWALK) continue;
      if (r.class === 'rail') {
        // railway: a ballast bed three tiles wide; the track (sleepers and two rails) on the centre tile. At a level crossing the
        // road surface shows through with the rails laid across it and a warning cross on both sides
        const inter0 = (fl & both) === both;
        if (strokes) { // the path world strokes the railway (strokePaths); a platform is a tile thing and stays one
          const railAxis: 'v' | 'h' = fl & RF_V ? 'v' : 'h';
          const centre = railAxis === 'v' ? (fl & RF_VC) !== 0 : (fl & RF_HC) !== 0;
          if (!inter0 && !centre && !bridge && platformAt(gen, c.x0 + lx, c.y0 + ly, railAxis)) {
            p.rect(lx, ly, 1, 1, '#b8b4ac');
            const towards = gen.net.hitsAt(c.x0 + lx, c.y0 + ly).find((h) => h.road.class === 'rail')?.off ?? 0;
            if (railAxis === 'v') p.rect(towards < 0 ? lx + 0.8 : lx + 0.1, ly, 0.1, 1, '#e8c840'); else p.rect(lx, towards < 0 ? ly + 0.8 : ly + 0.1, 1, 0.1, '#e8c840');
          }
          continue;
        }
        const railAxis: 'v' | 'h' = inter0 ? (gen.net.hitsAt(c.x0 + lx, c.y0 + ly).find((h) => h.road.class === 'rail')?.axis ?? 'v') : fl & RF_V ? 'v' : 'h';
        const centre = railAxis === 'v' ? (fl & RF_VC) !== 0 : (fl & RF_HC) !== 0;
        if (inter0) p.rect(lx, ly, 1, 1, '#4b4b4f');
        else if (!centre && platformAt(gen, c.x0 + lx, c.y0 + ly, railAxis)) {
          // a platform on the ballast beside the halt (the 14 tiles before an avenue crossing in town): paving and a yellow safety line
          p.rect(lx, ly, 1, 1, '#b8b4ac');
          if (railAxis === 'v') { const towards = gen.net.hitsAt(c.x0 + lx, c.y0 + ly).find((h) => h.road.class === 'rail')?.off ?? 0; p.rect(towards < 0 ? lx + 0.8 : lx + 0.1, ly, 0.1, 1, '#e8c840'); }
          else { const towards = gen.net.hitsAt(c.x0 + lx, c.y0 + ly).find((h) => h.road.class === 'rail')?.off ?? 0; p.rect(lx, towards < 0 ? ly + 0.8 : ly + 0.1, 1, 0.1, '#e8c840'); }
        }
        else { p.rect(lx, ly, 1, 1, bridge ? '#5a5d63' : r.color); if (!bridge && !centre && f.speckle(c.x0 + lx, c.y0 + ly) > 0.6) p.rect(lx + 0.3, ly + 0.4, 0.25, 0.2, r.edgeColor ?? '#5a544b'); }
        if (bridge) for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) { // a parapet where the deck meets open water (owner, Sep 8: the railway crosses on a bridge now)
          const j = at(lx + dx, ly + dy);
          if (j < 0 || c.road[j] || !isWater(j)) continue;
          if (dx) p.rect(dx > 0 ? lx + 0.85 : lx, ly, 0.15, 1, '#b9bcc1'); else p.rect(lx, dy > 0 ? ly + 0.85 : ly, 1, 0.15, '#b9bcc1');
        }
        if (centre) {
          if (railAxis === 'v') {
            if (!inter0) { p.rect(lx + 0.1, ly + 0.2, 0.8, 0.14, '#4a3a2a'); p.rect(lx + 0.1, ly + 0.7, 0.8, 0.14, '#4a3a2a'); }
            p.rect(lx + 0.25, ly, 0.1, 1, '#c8c4bc'); p.rect(lx + 0.65, ly, 0.1, 1, '#c8c4bc');
          } else {
            if (!inter0) { p.rect(lx + 0.2, ly + 0.1, 0.14, 0.8, '#4a3a2a'); p.rect(lx + 0.7, ly + 0.1, 0.14, 0.8, '#4a3a2a'); }
            p.rect(lx, ly + 0.25, 1, 0.1, '#c8c4bc'); p.rect(lx, ly + 0.65, 1, 0.1, '#c8c4bc');
          }
        }
        continue;
      }
      if (bridge && !strokes) {
        // railings where the deck meets open water
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
          const j = at(lx + dx, ly + dy);
          if (j < 0 || c.road[j]) continue;
          if (!isWater(j)) continue;
          if (brokenRails?.has(`${c.x0 + lx + dx},${c.y0 + ly + dy}`)) { // smashed: a stub either end and a gap
            if (dx) { p.rect(dx > 0 ? lx + 0.85 : lx, ly, 0.15, 0.2, '#c9ccd1'); p.rect(dx > 0 ? lx + 0.85 : lx, ly + 0.8, 0.15, 0.2, '#c9ccd1'); } else { p.rect(lx, dy > 0 ? ly + 0.85 : ly, 0.2, 0.15, '#c9ccd1'); p.rect(lx + 0.8, dy > 0 ? ly + 0.85 : ly, 0.2, 0.15, '#c9ccd1'); }
            continue;
          }
          if (dx) p.rect(dx > 0 ? lx + 0.85 : lx, ly, 0.15, 1, '#c9ccd1'); else p.rect(lx, dy > 0 ? ly + 0.85 : ly, 1, 0.15, '#c9ccd1');
        }
      }
      // a road that simply stops (segment end, shore): red/white barrier across the last tile
      if (!bridge && !strokes && (fl & both) !== both) {
        const ends: [number, number][] = fl & RF_V ? [[0, -1], [0, 1]] : [[-1, 0], [1, 0]];
        for (const [dx, dy] of ends) {
          const j = at(lx + dx, ly + dy);
          if (j < 0 || c.road[j]) continue;
          // (track 2) on the path network a wide road meets a narrower one at a node, and the wide road's shoulder tiles
          // end while its middle carries on — that is a junction, not a dead end. A barrier only where no road at all
          // continues within four tiles across; a road's end beside another road is left bare too, which reads right.
          let ahead = false;
          for (let k = -4; k <= 4 && !ahead; k++) { const jj = dy ? at(lx + k, ly + dy) : at(lx + dx, ly + k); if (jj >= 0 && c.road[jj]) ahead = true; }
          if (ahead) continue;
          const stripes = 4;
          for (let s = 0; s < stripes; s++) {
            const col = s % 2 ? '#e8e8e8' : '#d8382a';
            if (dy) p.rect(lx + s / stripes, dy < 0 ? ly + 0.05 : ly + 0.75, 1 / stripes, 0.2, col); else p.rect(dx < 0 ? lx + 0.05 : lx + 0.75, ly + s / stripes, 0.2, 1 / stripes, col);
          }
        }
      }
      const inter = (fl & both) === both;
      if (inter) {
        // a level crossing (a coarser road's junction with the railway): rails across the road on the track's centre line, no stripes
        const railHit = strokes ? undefined : gen.net.hitsAt(c.x0 + lx, c.y0 + ly).find((h) => h.road.class === 'rail' && Math.abs(h.off) <= 1);
        if (railHit) {
          if (railHit.off === 0) { if (railHit.axis === 'v') { p.rect(lx + 0.25, ly, 0.1, 1, '#c8c4bc'); p.rect(lx + 0.65, ly, 0.1, 1, '#c8c4bc'); } else { p.rect(lx, ly + 0.25, 1, 0.1, '#c8c4bc'); p.rect(lx, ly + 0.65, 1, 0.1, '#c8c4bc'); } }
          continue;
        }
        // crosswalk stripes on the intersection's outer rows/columns
        const up = at(lx, ly - 1), down = at(lx, ly + 1), left = at(lx - 1, ly), right = at(lx + 1, ly);
        // (a stripe row only where the carriageway carries on past the crossing — not where a wider segment's extra rows just end)
        const carries = (j: number) => j >= 0 && !!c.road[j] && (c.roadFlags[j] & both) !== both && !(c.roadFlags[j] & RF_SIDEWALK);
        const edgeV = carries(up) || carries(down);
        const edgeH = carries(left) || carries(right);
        if (edgeV && !edgeH) for (let k = 0; k < 3; k++) p.rect(lx + 0.1 + k * 0.3, ly + 0.15, 0.18, 0.7, '#d8d8d8');
        else if (edgeH && !edgeV) for (let k = 0; k < 3; k++) p.rect(lx + 0.15, ly + 0.1 + k * 0.3, 0.7, 0.18, '#d8d8d8');
        continue;
      }
      const m = r.markings;
      if (m?.center && !strokes) {
        if (fl & RF_VC) {
          const x = lx + (fl & RF_VEVEN ? 1 : 0.5); // even widths: the road covers [pos-(w/2-1), pos+w/2], its centre is the pos|pos+1 boundary
          if (!m.dashed || (c.y0 + ly) % 2 === 0) p.rect(x - 0.06, ly, 0.12, 1, m.center);
        }
        if (fl & RF_HC) {
          const y = ly + (fl & RF_HEVEN ? 1 : 0.5);
          if (!m.dashed || (c.x0 + lx) % 2 === 0) p.rect(lx, y - 0.06, 1, 0.12, m.center);
        }
      }
    }
  }
  }
  p.end();
  yield;
  p.begin(ox, oy, scale); // (the road phases ended their transform above: the texture is drawn in tile units)
  // (track 2, owner Sep 11: "dirt roads should not be just a single colour of brown") the dirt road's texture: stones,
  // dust and worn patches hashed per tile, over the surface and under everything drawn on it
  for (let ly = 0; ly < CHUNK; ly++) for (let lx = 0; lx < CHUNK; lx++) {
    const i = ly * CHUNK + lx, ri = c.road[i]; if (!ri) continue;
    const r = gen.roadList[ri - 1]; if (r.class !== 'dirt' || c.deckH[i] > 0 || (c.roadFlags[i] & RF_SIDEWALK)) continue;
    const x = c.x0 + lx, y = c.y0 + ly, h0 = f.speckle(x * 7 + 1, y * 3 + 2), h1 = f.speckle(x * 5 + 3, y * 11 + 7), h2 = f.speckle(x * 13 + 5, y * 17 + 1);
    const dark = '#6b5741', pale = '#b39c78', mid = r.edgeColor ?? '#7a6548';
    // a worn patch: a darker, damp-looking blotch on some tiles
    if (h0 > 0.62) p.circle(lx + 0.3 + h1 * 0.4, ly + 0.3 + h2 * 0.4, 0.28 + h0 * 0.22, h0 > 0.85 ? dark : mid);
    // stones and dry crumbs
    if (h1 > 0.4) p.circle(lx + 0.1 + h2 * 0.8, ly + 0.1 + h0 * 0.8, 0.08 + h1 * 0.07, h2 > 0.5 ? pale : dark);
    if (h2 > 0.55) p.circle(lx + 0.1 + h0 * 0.8, ly + 0.1 + h1 * 0.8, 0.06, h0 > 0.5 ? pale : dark);
  }
  p.end();
  // entrance steps: a doormat just outside every door so entrances read from the street
  p.begin(ox, oy, scale);
  for (const b of c.buildings) {
    const plan = floorPlan(b, 0, gen.seed);
    if (!plan) continue;
    for (const [dx, dy] of plan.doors) {
      const lx = dx - c.x0, ly = dy - c.y0;
      const [sx, sy] = b.rot === 0 ? [lx, ly + 1] : b.rot === 2 ? [lx, ly - 0.5] : b.rot === 1 ? [lx - 0.5, ly] : [lx + 1, ly];
      if (sx < -1 || sy < -1 || sx > CHUNK || sy > CHUNK) continue;
      if (b.rot % 2 === 0) { p.rect(sx + 0.05, sy, 0.9, 0.5, '#b9b3a8'); p.rect(sx + 0.05, sy + (b.rot === 0 ? 0.42 : 0), 0.9, 0.08, '#8e887d'); }
      else { p.rect(sx, sy + 0.05, 0.5, 0.9, '#b9b3a8'); p.rect(sx + (b.rot === 3 ? 0.42 : 0), sy + 0.05, 0.08, 0.9, '#8e887d'); }
    }
  }
  p.end();
  yield;
  // props (street furniture) with a small shadow when tall
  for (const pr of c.props) {
    const def = gen.propList[pr.prop];
    const sp = def.sprite;
    const px = ox + (pr.x - c.x0 + 0.5 - sp.size[0] / 2) * scale, py = oy + (pr.y - c.y0 + 0.5 - sp.size[1] / 2) * scale;
    if (def.height > 0) drawSpriteCached(p, shadowOf(def.id, sp), px + (0.28 * def.height * 0.35) * scale, py + (0.2 * def.height * 0.35) * scale, scale);
    drawSpriteCached(p, sp, px, py, scale);
  }
  // decor
  for (const d of c.decor) {
    const t = gen.terrainList[d.terrain];
    const dd = t.decor![d.index];
    const s = dd.sprite;
    drawSpriteCached(p, s, ox + (d.x - c.x0 + 0.5 - s.size[0] / 2) * scale, oy + (d.y - c.y0 + 0.5 - s.size[1] / 2) * scale, scale);
  }
}

const shadowCache = new Map<string, ReturnType<typeof silhouette>>();
function shadowOf(id: string, sp: Parameters<typeof silhouette>[0]) { let v = shadowCache.get(id); if (!v) { v = silhouette(sp, '#00000038'); shadowCache.set(id, v); } return v; }

/** Draw a building's roof sprite flat at its world rect (top-down map view, no extrusion). */
export function renderRoofFlat(p: Painter, b: PlacedBuilding, ox: number, oy: number, scale: number, shadeFactor = 1): void {
  const [fw, fd] = b.def.footprint;
  const cx = ox + (b.x + b.w / 2) * scale, cy = oy + (b.y + b.h / 2) * scale;
  const sprite = shadeFactor === 1 ? b.def.roof : tintSprite(b.def.roof, shadeFactor);
  drawSprite(p, sprite, cx - (fw / 2) * scale, cy - (fd / 2) * scale, scale, (b.rot * Math.PI) / 2);
}

/** Whole-chunk flat render (ground + roofs), for map previews and tests. */
export function renderChunkFlat(p: Painter, gen: WorldGen, c: Chunk, ox: number, oy: number, scale: number): void {
  renderChunkGround(p, gen, c, ox, oy, scale);
  const cx0 = ox - c.x0 * scale, cy0 = oy - c.y0 * scale;
  for (const b of c.buildings) renderRoofFlat(p, b, cx0, cy0, scale);
}

/**
 * (§40) A road's curve as the renderer strokes it, in world tiles: the edge's samples shifted half a tile onto the tile
 * centres, and another half across for an even width, whose centre is a tile boundary (`RF_VEVEN`) — so the stroke lies
 * on the tiles that carry the same road. Shared by the ground pass here and the deck layer in the renderer.
 */
export function strokePolyline(s: ReturnType<PathRoads['strokesIn']>[number]): [number, number][] {
  const { e, info } = s;
  const even = (info.road.width & 1) === 0;
  const ox = 0.5 + (even && info.axis === 'v' ? 0.5 : 0), oy = 0.5 + (even && info.axis === 'h' ? 0.5 : 0);
  const pts: [number, number][] = [];
  for (let i = 0; i < e.pts.length; i += 2) pts.push([e.pts[i] + ox, e.pts[i + 1] + oy]);
  return pts;
}

/** a ramp's foot on a stroke: where its own deck profile goes from 0 to raised — the row line `axis = m` the tiles use
 *  (`PathRoads.edgeHit` reads the deck by row), `s` the side of it the deck is on (+1: rows ≥ m) */
export type DeckFoot = { axis: 'h' | 'v'; m: number; s: 1 | -1 };
/**
 * (C, Sep 25 — bug reports, Sep 23: "car renders under the bridge here") the stroke's polyline with a point added exactly
 * where each ramp foot's row line crosses it, so the ground stroke and the deck layer both stop and start on the line the
 * tiles change on. The polyline's points are ~5 tiles apart and both passes classified by segment: the ground stroke
 * stopped up to half a segment short of the foot and the deck was painted two samples (two tiles) back over the ground
 * road to cover the gap — over tiles with no deck, where a car on the ground is drawn before the decks and was covered.
 */
export function deckFeet(pts: [number, number][], info: { deck?: Float32Array; u0: number; axis: 'h' | 'v' }): { pts: [number, number][]; feet: (DeckFoot | undefined)[] } {
  const { deck, u0, axis } = info;
  if (!deck || pts.length < 2) return { pts, feet: pts.map(() => undefined) };
  const k = axis === 'v' ? 1 : 0, on = (row: number): boolean => { const j = row - u0; return j >= 0 && j < deck.length && deck[j] > 0; };
  const out: [number, number][] = [pts[0]], feet: (DeckFoot | undefined)[] = [undefined];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i], ca = a[k], cb = b[k];
    if (ca !== cb) {
      const up = cb > ca, lo = Math.ceil(Math.min(ca, cb)), hi = Math.floor(Math.max(ca, cb));
      for (let n = 0; n <= hi - lo; n++) {
        const m = up ? lo + n : hi - n; if (m === ca || m === cb) continue; // (on a sample already: the row test at the sample decides)
        const below = on(m - 1), above = on(m); if (below === above) continue;
        const t = (m - ca) / (cb - ca);
        out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]); feet.push({ axis, m, s: above ? 1 : -1 });
      }
    }
    out.push(b); feet.push(undefined);
  }
  return { pts: out, feet };
}

/**
 * (track 1, §40) The path network's roads drawn along their curves. On the path world the road tiles are not painted
 * at all — the tiles stay the model (collisions, lots, hits, the crosswalks and rails drawn on top) and the picture is
 * these strokes: three layers, each for every road before the next, so a street's pavement never lands on the avenue
 * it joins — the pavement band (road plus both sidewalks), the carriageway, then the centre line as dashes measured
 * along the curve. The curve is shifted half a tile onto the tile centres (and another half across for an even width,
 * whose centre is a tile boundary — `RF_VEVEN`), so the stroke lies on the tiles that carry the same road. An end that
 * stops in the open — a dead end, or the shore of water the road has no tiles over — gets a square end and the
 * red/white barrier across it; a junction end is cut back by however much this road is wider than the crossing one,
 * so a wide stem's cap stays inside the through road; a centre line stops short of a junction by the widest crossing
 * road's half-width, where the crosswalk stripes take over. Over water the band and the carriageway take the bridge
 * deck's colours and the railings run along both edges, with a gap wherever a railing is smashed.
 */
function strokePaths(p: Painter, gen: WorldGen, c: Chunk, strokes: ReturnType<PathRoads['strokesIn']>, isWater: (i: number) => boolean, brokenRails?: ReadonlySet<string>): void {
  // what lies under each piece of a curve: land, a bridge deck, or a gap — water the road has no tiles over (a wet run
  // longer than its class's longest deck)
  // …and a raised deck (an overpass, a bridge on the deck layer — `Chunk.deckH`), which the renderer draws itself: the
  // ground stroke stops under the ramp foot with no barrier, since the road goes on above
  type Kind = 0 | 1 | 2 | 3; const LAND = 0, DECK = 1, GAP = 2, RAISED = 3;
  type Run = { pts: [number, number][]; kind: Kind; /** arc positions where the run stops in the open (a gap, a dead end): the square end goes there */ open0?: number; open1?: number };
  const f = gen.fields, scratch: RoadHit[] = [];
  const under = (s: (typeof strokes)[number], x: number, y: number): Kind => {
    const tx = Math.floor(x), ty = Math.floor(y), lx = tx - c.x0, ly = ty - c.y0;
    // raised by *this* road's own profile — the tile's deck height under a viaduct belongs to the road carried over
    const { deck, u0, axis } = s.info, j = (axis === 'v' ? ty : tx) - u0;
    if (deck && j >= 0 && j < deck.length && deck[j] > 0) return RAISED;
    let road: boolean, water: boolean;
    if (lx >= 0 && ly >= 0 && lx < CHUNK && ly < CHUNK) { const i = ly * CHUNK + lx; road = c.road[i] !== 0; water = isWater(i); }
    else { // (a tile past this chunk: no tile to read — the hits, minus what a place keeps roads out of, as the generator would decide it)
      const hits = gen.net.hitsAt(tx, ty, scratch), mode = gen.places.at(tx, ty, !gen.places.learnOnly)?.roads ?? 'keep';
      road = mode !== 'none' && hits.some((h) => mode !== 'major' || h.level === 0);
      const t = f.terrainAt(x, y); water = !t.walkable && !t.buildable;
    }
    return !road ? GAP : water ? DECK : LAND;
  };
  type Line = { s: (typeof strokes)[number]; pts: [number, number][]; len: number[]; kind: Kind[]; total: number };
  const lines: Line[] = [];
  for (const s of strokes) {
    const pts = deckFeet(strokePolyline(s), s.info).pts; // (Sep 25) a point on each ramp foot's row line: the ground stops exactly there
    const len = [0], kind: Kind[] = [];
    for (let i = 1; i < pts.length; i++) { len.push(len[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1])); kind.push(under(s, (pts[i][0] + pts[i - 1][0]) / 2, (pts[i][1] + pts[i - 1][1]) / 2)); }
    lines.push({ s, pts, len, kind, total: len[len.length - 1] });
  }
  const seg = (l: Line, d: number): number => { let i = 1; while (i < l.len.length - 1 && l.len[i] < d) i++; return i; };
  /** the point at arc length d (extrapolated along the end segments past either end) */
  const at = (l: Line, d: number): [number, number] => { const i = seg(l, d), a = l.pts[i - 1], b = l.pts[i], span = l.len[i] - l.len[i - 1] || 1, k = (d - l.len[i - 1]) / span; return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k]; };
  /** the unit tangent at arc length d */
  const tangent = (l: Line, d: number): [number, number] => { const i = seg(l, d), a = l.pts[i - 1], b = l.pts[i], n = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1; return [(b[0] - a[0]) / n, (b[1] - a[1]) / n]; };
  /** how far along the road a row line (square to the road's axis) runs from the centre to an edge `hw` out, plus half a tile */
  const slant = (l: Line, d: number, hw: number): number => { const [tx, ty] = tangent(l, d), along = Math.abs(l.s.info.axis === 'v' ? ty : tx), across = Math.abs(l.s.info.axis === 'v' ? tx : ty); return Math.min(2, across / Math.max(0.3, along)) * Math.max(hw, l.s.info.total / 2) + 0.5; };
  /**
   * the polyline between two arc lengths, split into runs by what is under it, the gaps left out; a run that ends at
   * a gap, or at an end of the road that is a dead end, is cut back by `inset` (the stroke's half-width) and remembers
   * where the open end was, so the square end can be drawn there instead of the round cap
   */
  const slice = (l: Line, from: number, to: number, inset = 0, open: [boolean, boolean] = [false, false]): Run[] => {
    const { len, kind, total } = l;
    from = Math.max(0, from); to = Math.min(total, to);
    if (to - from < 0.2) return [];
    const spans: { d0: number; d1: number; kind: Kind }[] = [];
    for (let i = 1; i < len.length; i++) {
      const d0 = Math.max(from, len[i - 1]), d1 = Math.min(to, len[i]);
      if (d1 <= d0) continue;
      const last = spans[spans.length - 1];
      if (last && last.kind === kind[i - 1]) last.d1 = d1; else spans.push({ d0, d1, kind: kind[i - 1] });
    }
    const runs: Run[] = [];
    for (let k = 0; k < spans.length; k++) {
      const sp = spans[k]; if (sp.kind === GAP || sp.kind === RAISED) continue;
      // a sliver of road beside a gap — the one segment that straddled a place's edge, classified by its midpoint — is
      // not a road: it drew a wedge with a barrier on it in the middle of a base's gravel (owner, Sep 11: "weird kind of leftover")
      if (sp.d1 - sp.d0 < 2.5 && ((k > 0 && spans[k - 1].kind === GAP) || (k < spans.length - 1 && spans[k + 1].kind === GAP))) continue;
      // (Sep 25) nor the tile or two of ground between a ramp's foot and the node it ends on: the road it runs into covers it, and its round
      // cap drew a disc of its own colour on that road (the feet split the segments exactly now, so the sliver is a run of its own)
      if (sp.kind === LAND && sp.d1 - sp.d0 < 2.5 && ((k === 0 && spans.length > 1 && spans[1].kind === RAISED) || (k === spans.length - 1 && k > 0 && spans[k - 1].kind === RAISED))) continue;
      let d0 = sp.d0, d1 = sp.d1;
      const run: Run = { pts: [], kind: sp.kind };
      // (a dead end's `from`/`to` already carry the caller's inset: cutting back again left the round cap poking out
      // half a road past the square end — owner, Sep 10: "the road here looks weird" at a lane's end)
      if (k > 0 && spans[k - 1].kind === GAP) { run.open0 = d0; d0 += inset; } else if (k === 0 && open[0]) run.open0 = -0.5;
      if (k < spans.length - 1 && spans[k + 1].kind === GAP) { run.open1 = d1; d1 -= inset; } else if (k === spans.length - 1 && open[1]) run.open1 = total + 0.5;
      // (Sep 25) at a ramp's foot the ground goes on under the deck by as much as the foot's row line runs slantwise across the road:
      // the deck is cut on that line (renderer decksStroked), and the ground's square-across end left a wedge of nothing beside it
      if (k > 0 && spans[k - 1].kind === RAISED) d0 = Math.max(from, d0 - slant(l, d0, inset));
      if (k < spans.length - 1 && spans[k + 1].kind === RAISED) d1 = Math.min(to, d1 + slant(l, d1, inset));
      if (d1 - d0 < 0.2) continue;
      run.pts.push(at(l, d0));
      for (let i = 1; i < len.length; i++) if (len[i] > d0 && len[i] < d1) run.pts.push(l.pts[i]);
      run.pts.push(at(l, d1));
      runs.push(run);
    }
    return runs;
  };
  const local = (pts: [number, number][]): [number, number][] => pts.map(([x, y]) => [x - c.x0, y - c.y0]);
  /** a square end: the quad from the open end back to where the cut-back stroke starts, straight along the tangent there */
  const squareEnd = (l: Line, dOpen: number, inward: 1 | -1, hw: number, colour: string, barrier: boolean): void => {
    // squared off along the tangent *where the cut-back stroke ends*, so the quad's base is the stroke's own cap and a
    // bend in the last half-width does not leave the round cap poking out beside the box (owner, Sep 10: "the road
    // here looks weird" at a curling dead end) — the stub past that runs straight, which a dead end may
    const dIn = Math.max(0.01, Math.min(l.total - 0.01, dOpen + inward * hw));
    // the direction into the road there, over a few tiles rather than one segment (the polyline steps row by row)
    const [ix, iy] = at(l, dIn), [jx, jy] = at(l, dIn + inward * Math.min(4, hw)), jl = Math.hypot(jx - ix, jy - iy) || 1;
    const tx = ((jx - ix) / jl) * inward, ty = ((jy - iy) / jl) * inward; // (as `tangent`: along increasing d)
    const nx = -ty, ny = tx, ex = ix - inward * tx * hw, ey = iy - inward * ty * hw;
    p.poly(local([[ex + nx * hw, ey + ny * hw], [ix + nx * hw, iy + ny * hw], [ix - nx * hw, iy - ny * hw], [ex - nx * hw, ey - ny * hw]]), colour);
    if (!barrier) return;
    // the red/white barrier across the last half tile, four stripes
    const bx = ex + inward * tx * 0.3, by = ey + inward * ty * 0.3;
    for (let k = 0; k < 4; k++) { const a = -hw + (k * 2 * hw) / 4, b = a + (2 * hw) / 4; p.line(local([[bx + nx * a, by + ny * a], [bx + nx * b, by + ny * b]]), k % 2 ? '#e8e8e8' : '#d8382a', 0.24); }
  };
  // layer 1: the pavement band; layer 2: the carriageway, with the square ends and barriers where a road stops in the open
  const decks: { l: Line; run: Run }[] = [];
  // the railway (Sep 11): its ballast goes under everything, the roads cross it at grade, and the sleepers and rails go
  // on last — sleepers only on the ballast, the rails across the road at a level crossing
  const railLines = lines.filter((l) => l.s.info.road.class === 'rail'), roadLines = lines.filter((l) => l.s.info.road.class !== 'rail');
  for (const l of railLines) for (const run of slice(l, 0, l.total)) p.line(local(run.pts), run.kind === DECK ? '#5a5d63' : l.s.info.road.color, l.s.info.total);
  // the carriageways finest road first, so a track's end (its cap reaches the node) lies under the avenue it joins,
  // never on it (owner, Sep 10: "dirt road goes on top of asphalt here"); the pavement bands coarsest first as before
  for (const layer of [0, 1] as const) for (const l of layer === 0 ? roadLines : roadLines.slice().reverse()) {
    const { info, dead, box } = l.s, r = info.road;
    const w = layer === 0 ? info.total : r.width;
    if (layer === 0 && !(r.sidewalk ?? 0)) continue;
    const hw = w / 2;
    const inset = (k: 0 | 1): number => (dead[k] ? hw : box[k] ? Math.max(0, hw - (box[k] / 2 - (layer === 1 ? 1 : 0))) : 0);
    for (const run of slice(l, inset(0), l.total - inset(1), hw, dead)) {
      const colour = layer === 0 ? (run.kind === DECK ? '#8a8f96' : r.sidewalkColor ?? '#b0b0ab') : run.kind === DECK ? '#5a5d63' : r.color;
      p.line(local(run.pts), colour, w);
      if (run.open0 !== undefined) squareEnd(l, run.open0, 1, hw, colour, layer === 1);
      if (run.open1 !== undefined) squareEnd(l, run.open1, -1, hw, colour, layer === 1);
      if (layer === 1 && run.kind === DECK) decks.push({ l, run });
    }
  }
  // the railings: a light line along both edges of every deck, broken where a railing was smashed
  for (const { run, l } of decks) {
    const hw = l.s.info.total / 2 - 0.12;
    for (const side of [-1, 1] as const) {
      let cur: [number, number][] = [];
      const flush = () => { if (cur.length > 1) p.line(local(cur), '#c9ccd1', 0.16); cur = []; };
      for (let i = 0; i < run.pts.length; i++) {
        const a = run.pts[Math.max(0, i - 1)], b = run.pts[Math.min(run.pts.length - 1, i + 1)];
        const n = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1, nx = (-(b[1] - a[1]) / n) * side, ny = ((b[0] - a[0]) / n) * side;
        const q: [number, number] = [run.pts[i][0] + nx * hw, run.pts[i][1] + ny * hw];
        // the water tile just outside this railing is where a smash is recorded
        if (brokenRails?.has(`${Math.floor(q[0] + nx * 0.8)},${Math.floor(q[1] + ny * 0.8)}`)) { flush(); continue; }
        cur.push(q);
      }
      flush();
    }
  }
  // the track: sleepers every half tile where the ballast shows, then the two rails the whole way
  const roadUnder = (x: number, y: number): boolean => { const lx = Math.floor(x) - c.x0, ly = Math.floor(y) - c.y0; if (lx < 0 || ly < 0 || lx >= CHUNK || ly >= CHUNK) return gen.net.hitsAt(Math.floor(x), Math.floor(y), scratch).some((h) => h.road.class !== 'rail'); const ri = c.road[ly * CHUNK + lx]; return ri !== 0 && gen.roadList[ri - 1].class !== 'rail'; };
  for (const l of railLines) {
    for (const run of slice(l, 0, l.total)) {
      if (run.kind === DECK) { // a parapet along both edges of a bridge over water
        const hw = l.s.info.total / 2 - 0.1;
        for (const side of [-1, 1] as const) p.line(local(run.pts.map((q, i) => { const a = run.pts[Math.max(0, i - 1)], b = run.pts[Math.min(run.pts.length - 1, i + 1)]; const n = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1; return [q[0] - ((b[1] - a[1]) / n) * side * hw, q[1] + ((b[0] - a[0]) / n) * side * hw] as [number, number]; })), '#b9bcc1', 0.16);
      }
    }
    for (let d = 0.25; d < l.total; d += 0.5) {
      const [x, y] = at(l, d); if (under(l.s, x, y) !== LAND && under(l.s, x, y) !== DECK) continue; if (roadUnder(x, y)) continue;
      const [tx, ty] = tangent(l, d), nx = -ty, ny = tx;
      p.line(local([[x + nx * 0.42, y + ny * 0.42], [x - nx * 0.42, y - ny * 0.42]]), '#4a3a2a', 0.16);
    }
    for (const run of slice(l, 0, l.total)) for (const side of [-1, 1] as const) p.line(local(run.pts.map((q, i) => { const a = run.pts[Math.max(0, i - 1)], b = run.pts[Math.min(run.pts.length - 1, i + 1)]; const n = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1; return [q[0] - ((b[1] - a[1]) / n) * side * 0.2, q[1] + ((b[0] - a[0]) / n) * side * 0.2] as [number, number]; })), '#c8c4bc', 0.1);
  }
  // layer 3: the centre line, dashed along the curve
  for (const l of roadLines) {
    const m = l.s.info.road.markings; if (!m?.center) continue;
    const from = l.s.box[0] / 2, to = l.total - l.s.box[1] / 2;
    if (!m.dashed) { for (const run of slice(l, from, to)) p.line(local(run.pts), m.center, 0.12); continue; }
    for (let d = Math.ceil(from / 3) * 3; d < to; d += 3) for (const run of slice(l, Math.max(from, d), Math.min(to, d + 1.5))) p.line(local(run.pts), m.center, 0.12);
  }
  // layer 4 (owner, Sep 13: "add manhole covers to roads, just visuals"): a cover every `manholes` metres along the carriageway,
  // on the lanes — alternating sides of the centre line — never in a junction or over a gap; the phase is the stroke's own
  const crossingAt = (x: number, y: number): boolean => { const tx = Math.floor(x), ty = Math.floor(y), lx = tx - c.x0, ly = ty - c.y0; if (lx >= 0 && ly >= 0 && lx < CHUNK && ly < CHUNK) { const fl = c.roadFlags[ly * CHUNK + lx]; return (fl & (RF_V | RF_H)) === (RF_V | RF_H); } const hits = gen.net.hitsAt(tx, ty, scratch); return hits.some((h) => h.axis === 'v') && hits.some((h) => h.axis === 'h'); };
  for (const l of roadLines) {
    const m = l.s.info.road.markings; if (!m?.manholes) continue;
    const from = l.s.box[0] / 2 + 2, to = l.total - l.s.box[1] / 2 - 2, step = m.manholes, off = Math.max(1.2, l.s.info.road.width / 4);
    const phase = hash01(31, Math.round(l.pts[0][0]), Math.round(l.pts[0][1])) * step;
    let k = 0;
    for (let d = from + phase; d < to; d += step, k++) {
      const [x, y] = at(l, d), u = under(l.s, x, y); if ((u !== LAND && u !== DECK) || crossingAt(x, y)) continue;
      const [tx, ty] = tangent(l, d), side = k % 2 ? 1 : -1, cx = x - ty * side * off, cy = y + tx * side * off;
      const [[px, py]] = local([[cx, cy]]);
      p.circle(px, py, 0.4, '#3b3c3f', '#232427', 0.08);
      p.circle(px, py, 0.26, undefined, '#4c4d51', 0.06);
      p.line([[px - 0.12, py], [px + 0.12, py]], '#2a2b2e', 0.05);
    }
  }
}
